import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import api from '../services/api';
import { ArrowLeft, DollarSign, Save } from 'lucide-react';

const LicensePricingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isDark } = useTheme();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pricing, setPricing] = useState({
    solo_vpn: { monthly: 0, semiannual: 0, annual: 0, lifetime: 0 },
    free: { monthly: 0, semiannual: 0, annual: 0, lifetime: 0 },
    todo_incluido: { monthly: 0, semiannual: 0, annual: 0, lifetime: 0 },
    solo_hotspot: { monthly: 0, semiannual: 0, annual: 0, lifetime: 0 },
    hotspot_pppoe: { monthly: 0, semiannual: 0, annual: 0, lifetime: 0 },
    solo_pppoe: { monthly: 0, semiannual: 0, annual: 0, lifetime: 0 },
    solo_herramientas: { monthly: 0, semiannual: 0, annual: 0, lifetime: 0 },
    extra_router_cost: 0,
    extra_seller_cost: 0,
    sellers_per_router: 3,
    bank_name: '',
    account_number: '',
    clabe: '',
    account_holder: '',
    payment_reference: ''
  });

  useEffect(() => {
    // Verificar que sea super_admin
    if (user?.role !== 'super_admin') {
      alert('⛔ Acceso denegado: Solo super administradores pueden ver esta página');
      navigate('/dashboard');
      return;
    }
    loadPricing();
  }, [user]);

  const loadPricing = async () => {
    try {
      const response = await api.get('/api/superadmin/license-pricing');
      setPricing(response.data);
    } catch (error) {
      console.error('Error cargando precios:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/api/superadmin/license-pricing', pricing);
      alert('✅ Configuración de precios guardada exitosamente');
    } catch (error) {
      console.error('Error guardando:', error);
      alert('❌ Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  const updatePlanPrice = (planKey, field, value) => {
    setPricing({
      ...pricing,
      [planKey]: {
        ...pricing[planKey],
        [field]: parseFloat(value) || 0
      }
    });
  };

  const plans = [
    { key: 'solo_vpn', name: 'SOLO VPN', color: 'blue', onlyLifetime: false },
    { key: 'free', name: 'FREE (Trial)', color: 'green', disabled: true, onlyLifetime: false },
    { key: 'todo_incluido', name: 'TODO INCLUIDO', color: 'purple', onlyLifetime: false },
    { key: 'solo_hotspot', name: 'SOLO HOTSPOT', color: 'orange', onlyLifetime: false },
    { key: 'hotspot_pppoe', name: 'HOTSPOT + PPPOE', color: 'pink', onlyLifetime: false },
    { key: 'solo_pppoe', name: 'SOLO PPPOE', color: 'teal', onlyLifetime: false },
    { key: 'solo_herramientas', name: 'SOLO HERRAMIENTAS', color: 'indigo', onlyLifetime: true }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDark 
        ? 'bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900' 
        : 'bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/super-admin')}
              className={`p-2 rounded-lg ${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`}
            >
              <ArrowLeft className={`w-6 h-6 ${isDark ? 'text-gray-300' : 'text-gray-700'}`} />
            </button>
            <div>
              <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Configuración de Precios
              </h1>
              <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                Define los precios de cada plan y costos adicionales
              </p>
            </div>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            <Save className="w-5 h-5" />
            {saving ? 'Guardando...' : 'Guardar Configuración'}
          </button>
        </div>

        {/* Costos Adicionales */}
        <div className={`mb-6 rounded-xl shadow-lg p-6 ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
          <h2 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            💰 Costos Adicionales
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Router Adicional ($/mes)
              </label>
              <input
                type="number"
                step="0.01"
                value={pricing.extra_router_cost}
                onChange={(e) => setPricing({...pricing, extra_router_cost: parseFloat(e.target.value) || 0})}
                className={`w-full px-4 py-2 rounded-lg border ${
                  isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
                placeholder="0.00"
              />
            </div>
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Vendedor Adicional ($/mes)
              </label>
              <input
                type="number"
                step="0.01"
                value={pricing.extra_seller_cost}
                onChange={(e) => setPricing({...pricing, extra_seller_cost: parseFloat(e.target.value) || 0})}
                className={`w-full px-4 py-2 rounded-lg border ${
                  isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
                placeholder="0.00"
              />
            </div>
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Vendedores por Router (incluidos)
              </label>
              <input
                type="number"
                value={pricing.sellers_per_router}
                onChange={(e) => setPricing({...pricing, sellers_per_router: parseInt(e.target.value) || 3})}
                className={`w-full px-4 py-2 rounded-lg border ${
                  isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
                placeholder="3"
              />
            </div>
          </div>
        </div>

        {/* Precios por Plan */}
        <div className="space-y-6">
          {plans.map((plan) => (
            <div
              key={plan.key}
              className={`rounded-xl shadow-lg p-6 ${isDark ? 'bg-gray-800' : 'bg-white'} ${
                plan.disabled ? 'opacity-60' : ''
              }`}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-3 h-3 rounded-full bg-${plan.color}-500`}></div>
                <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {plan.name}
                </h3>
                {plan.disabled && (
                  <span className="text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded">
                    Siempre $0 (Período de Prueba)
                  </span>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {!plan.onlyLifetime && (
                  <>
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        Mensual ($)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={pricing[plan.key].monthly}
                        onChange={(e) => updatePlanPrice(plan.key, 'monthly', e.target.value)}
                        disabled={plan.disabled}
                        className={`w-full px-4 py-2 rounded-lg border ${
                          isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                        } disabled:opacity-50`}
                        placeholder="0.00"
                      />
                    </div>

                    <div>
                      <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        Semestral ($)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={pricing[plan.key].semiannual}
                        onChange={(e) => updatePlanPrice(plan.key, 'semiannual', e.target.value)}
                        disabled={plan.disabled}
                        className={`w-full px-4 py-2 rounded-lg border ${
                          isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                        } disabled:opacity-50`}
                        placeholder="0.00"
                      />
                    </div>

                    <div>
                      <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        Anual ($)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={pricing[plan.key].annual}
                        onChange={(e) => updatePlanPrice(plan.key, 'annual', e.target.value)}
                        disabled={plan.disabled}
                        className={`w-full px-4 py-2 rounded-lg border ${
                          isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                        } disabled:opacity-50`}
                        placeholder="0.00"
                      />
                    </div>
                  </>
                )}

                <div className={plan.onlyLifetime ? 'md:col-span-4' : ''}>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Pago Único ($) {plan.onlyLifetime && '⭐ Único método de pago'}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={pricing[plan.key].lifetime}
                    onChange={(e) => updatePlanPrice(plan.key, 'lifetime', e.target.value)}
                    disabled={plan.disabled}
                    className={`w-full px-4 py-2 rounded-lg border ${
                      isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                    } disabled:opacity-50`}
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Datos Bancarios para Depósitos */}
        <div className={`mt-6 rounded-xl shadow-lg p-6 ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
          <h2 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            🏦 Datos Bancarios para Depósitos
          </h2>
          <p className={`text-sm mb-6 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Esta información se mostrará a los usuarios cuando deseen realizar un pago
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Nombre del Banco
              </label>
              <input
                type="text"
                value={pricing.bank_name}
                onChange={(e) => setPricing({...pricing, bank_name: e.target.value})}
                className={`w-full px-4 py-2 rounded-lg border ${
                  isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
                placeholder="Ej: BBVA, Santander, Banorte"
              />
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Número de Cuenta
              </label>
              <input
                type="text"
                value={pricing.account_number}
                onChange={(e) => setPricing({...pricing, account_number: e.target.value})}
                className={`w-full px-4 py-2 rounded-lg border ${
                  isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
                placeholder="1234567890"
              />
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                CLABE Interbancaria
              </label>
              <input
                type="text"
                value={pricing.clabe}
                onChange={(e) => setPricing({...pricing, clabe: e.target.value})}
                className={`w-full px-4 py-2 rounded-lg border ${
                  isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
                placeholder="012345678901234567"
                maxLength="18"
              />
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Titular de la Cuenta
              </label>
              <input
                type="text"
                value={pricing.account_holder}
                onChange={(e) => setPricing({...pricing, account_holder: e.target.value})}
                className={`w-full px-4 py-2 rounded-lg border ${
                  isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
                placeholder="Nombre del titular"
              />
            </div>

            <div className="md:col-span-2">
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Referencia/Concepto de Pago
              </label>
              <input
                type="text"
                value={pricing.payment_reference}
                onChange={(e) => setPricing({...pricing, payment_reference: e.target.value})}
                className={`w-full px-4 py-2 rounded-lg border ${
                  isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
                placeholder="Ej: Pago licencia EasyTickets"
              />
            </div>
          </div>
        </div>

        {/* Info */}
        <div className={`mt-6 p-4 rounded-lg ${isDark ? 'bg-blue-900/20' : 'bg-blue-50'}`}>
          <p className={`text-sm ${isDark ? 'text-blue-300' : 'text-blue-800'}`}>
            ℹ️ <strong>Nota:</strong> Los usuarios verán estos precios durante el proceso de selección de plan.
            El plan FREE siempre permanece en $0.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LicensePricingPage;
